'use client';

import React from 'react';
import { Box } from '../Box';
import { useTheme } from '../hooks/useTheme';
import { UIStyling } from '../styles/UIStyling';
import type { UIThemableComponent } from '../types/UI';
import { UIColors, UIShapes, UIShapesExtra, UISizes, UIVariants } from '../types/UI';
import { cn } from '../utils/classnames';

export type BadgeProps = UIThemableComponent & {
  ref?: React.ForwardedRef<HTMLDivElement>;
  fullWidth?: boolean;
  square?: boolean;
};

const BadgeComp = (
  {
    cname = 'ui-badge',
    children,
    className,

    // ----- Themable props defaults

    color = UIColors.default,
    size = UISizes.xs,
    variant = UIVariants.solid,
    shape = UIShapes.rounded,
    gradient,
    elevation,

    // ----- Options
    fullWidth,
    square = false,

    ...otherProps
  }: BadgeProps,
  forwardedRef: React.ForwardedRef<HTMLDivElement>
) => {
  const { theme } = useTheme();
  const themableProps = React.useMemo(() => {
    return { variant, color, shape, size, theme, gradient, elevation };
  }, [color, elevation, gradient, shape, size, theme, variant]);

  const isSquared: boolean = shape === UIShapesExtra.circle || square;

  const classes = React.useMemo(() => {
    let classes = UIVariants.unstyled.toString();

    if (variant !== UIVariants.unstyled) {
      classes = cn(
        cname,
        {
          [`px-2.5 py-0.5`]: size,
        },
        UIStyling.shapes(themableProps),
        { [`rounded`]: shape === UIShapes.rounded },
        {
          [`bg-${color}-100 text-${color}-800 dark:bg-${color}-900 dark:text-${color}-300`]:
            variant === UIVariants.solid && !gradient,
        },
        {
          [`bg-${color}-200`]: variant === UIVariants.solid && !gradient && color === UIColors.default,
        },
        {
          [`text-${color}-800 dark:text-${color}-300 bg-gradient-to-r from-${color}-100 via-${color}-200 to-${color}-300 dark:from-${color}-700 dark:via-${color}-800 dark:to-${color}-900 `]:
            variant === UIVariants.solid && gradient,
        },
        {
          [`dark:bg-${color}-700`]: color === UIColors.default && variant === UIVariants.solid,
        },
        {
          [`dark:bg-default-700 dark:text-${color}-400 border-${color}-400`]: variant === UIVariants.outline,
        },
        {
          [`dark:text-${color}-300`]: color === UIColors.warning && variant === UIVariants.outline,
        },
        {
          [`border-${color}-500`]: color === UIColors.default && variant === UIVariants.outline,
        },
        {
          [`border-${color}-300`]: color === UIColors.warning && variant === UIVariants.outline,
        },
        shape === UIShapesExtra.circle && UIStyling.squareSizesForm(themableProps),

        // control svg icon sizing for non squared buttons
        {
          ['[&>svg]:h-2.5 [&>svg]:w-2.5']: size == UISizes.xs && !isSquared,
          ['[&>svg]:h-3 [&>svg]:w-3']: size == UISizes.sm && !isSquared,
          ['[&>svg]:h-4 [&>svg]:w-4']: size == UISizes.md && !isSquared,
          ['[&>svg]:h-8 [&>svg]:w-8']: size == UISizes.lg && !isSquared,
          ['[&>svg]:h-12 [&>svg]:w-12']: size == UISizes.xl && !isSquared,
        },

        'inline-flex items-center gap-1 whitespace-nowrap',
        { [`flex`]: fullWidth },
        className
      );

      return classes;
    }
  }, [className, cname, color, fullWidth, gradient, isSquared, shape, size, themableProps, variant]);

  return (
    <Box noPad ref={forwardedRef} cname={cname} {...themableProps} {...otherProps} className={classes}>
      {children}
    </Box>
  );
};

export const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  BadgeComp
) as React.ForwardRefExoticComponentExtended<BadgeProps>;
// Badge.displayName = 'Badge';

export const BadgeShapes = [...Object.values(UIShapes), UIShapesExtra.pill];
